import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/Modules/Core/Components/shadcn/button';
import { cn } from '@/lib/utils';

type PaginationControlsProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  summary?: React.ReactNode;
  className?: string;
};

const getVisiblePages = (currentPage: number, totalPages: number) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: Array<number | string> = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) {
    pages.push('left-ellipsis');
  }

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (end < totalPages - 1) {
    pages.push('right-ellipsis');
  }

  pages.push(totalPages);

  return pages;
};

export function PaginationControls({
  currentPage,
  totalPages,
  onPageChange,
  summary,
  className,
}: PaginationControlsProps) {
  const visiblePages = getVisiblePages(currentPage, totalPages);

  return (
    <div
      className={cn(
        'flex flex-col gap-4 border-t border-border/40 pt-5 lg:flex-row lg:items-center lg:justify-between',
        className
      )}
    >
      <div className="text-[13px] text-muted-foreground">
        {summary ?? `Page ${currentPage} of ${totalPages}`}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>

        {visiblePages.map((item) =>
          typeof item === 'number' ? (
            <Button
              key={item}
              type="button"
              variant={item === currentPage ? 'default' : 'outline'}
              className="min-w-10 px-3"
              onClick={() => onPageChange(item)}
            >
              {item}
            </Button>
          ) : (
            <span
              key={item}
              className="px-1 text-[13px] text-muted-foreground"
            >
              ...
            </span>
          )
        )}

        <Button
          type="button"
          variant="outline"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          Next
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
